import React, { useEffect, useRef, useState } from 'react';
import { useScheduleActionLock } from '../../../hook/useScheduleActionLock';
import { Col, concatDateAndTimeDayjs, isSameDate, Row } from '../../../lib/utils';
import { Body3Regular, Subtitle1Bold } from '../../../lib/font';
import { colors } from '../../../lib/colors';
import WhiteSquareButton from '../../Button/WhiteSquareButton';
import ColorSquareButton from '../../Button/ColorSquareButton';
import SvgIcon from '../../SvgIcon';
import dayjs, { Dayjs } from 'dayjs';
import Calendar from '../../Calendar/Calendar';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createOneOnOneScheduleAPI,
    createOneOnTwoScheduleAPI,
    getMemberListForCenterAPI,
    createOneMoreScheduleAPI,
} from '../../../api/schedule';
import CreateOneMoreScheduleLeft from './CreateOneMoreScheduleLeft';
import CreateOneMoreScheduleRight from './CreateOneMoreScheduleRight';
import _, { replace } from 'lodash';
import { useNavigate } from 'react-router-dom';
import ModalMembershipCreate from '../../MemberProfile/ModalMembershipCreate';
import ModalRegisterPass from '../../../scenes/ClassPass/ModalRegisterPass/ModalRegisterPass';
import {
    buildWritableScheduleMemberOptions,
    isScheduleMemberPickerReadOnly,
    refreshSelectedScheduleMembers,
} from '../memberPicker';
import {
    buildOneMoreScheduleIntentKey,
    getSharedScheduleErrorMessage,
    getDuetFailureFollowUpNotice,
    getStableSharedScheduleOperation,
    isPostWriteSharedScheduleFailure,
    shouldDiscardSharedScheduleOperation,
    StableSharedScheduleOperation,
    useScheduleMembershipEntitlements,
} from '../../../features/sharedMembership';
import { refreshScheduleWriteQueries } from '../scheduleWriteCache';
import {
    buildScheduleConfirmAlertContent,
    resolveScheduleSubmitDecision,
    resolveScheduleSubmitPlan,
} from '../scheduleSubmitPlan';
import {
    findScheduleDateWarnings,
    formatScheduleDateWarningMessage,
    formatSharedDateBlockMessage,
    ScheduleDateWarningItem,
} from '../scheduleDateWarning';
import { dispatchScheduleSubmitDecision } from '../scheduleConfirmAttempt';
import { useScheduleConfirmDialog } from '../scheduleConfirmDialog';
import useMembershipPauseCreateGate from '../../MembershipPause/useMembershipPauseCreateGate';
import { compactTargets, toPtPauseGateTarget } from '../../../features/membershipPause/createGate';

type RefineDate = {
    refineStartTime: string;
    refineEndTime: string;
};

/**
 * 등록 버튼을 누른 순간 한 번 만든 **보낼 요청 내용**.
 *
 * 날짜 경고 판정·확인 창·전송이 모두 이 객체 하나를 쓴다. mutation 안에서 순번 상태로
 * 다시 만들지 않는다 — 창이 떠 있는 동안 목록이 다시 와도 확인한 수강권·시각 그대로 나간다.
 */
type OneMoreOneOnOneSubmission = {
    target: 'ONE_ON_ONE';
    centerId: string;
    coachId: string;
    coachLabel: string;
    memberId: string;
    membership: any;
    refineDate: RefineDate;
    intentKey: string;
};

/** 지금은 도달하지 않는 1:2 경로(아래 createOneOnTwoSchedule 주석). 보낼 body 는 예전 그대로다. */
type OneMoreOneOnTwoSubmission = {
    target: 'ONE_ON_TWO';
    centerId: string;
    coachId: string;
    scheduleTimes: Array<{
        memberId: string;
        startTime: string;
        endTime: string;
        membershipId?: string;
    }>;
    /** 판정 전용 — body 에는 싣지 않는다(예전과 같음). */
    memberships: any[];
    refineDate: RefineDate;
};

type OneMoreScheduleSubmission = OneMoreOneOnOneSubmission | OneMoreOneOnTwoSubmission;

const CreateOneMoreSchedule = ({
    coachOption,
    authorization,
    authObject,
    createAlert,
    selectedSchedule,
    calendarType,
    calendarSelectedDay,
    calendarScrollY,
    sharedMembershipStaffBookingEnabled,
}: any) => {
    /** 스케줄 동작 잠금(수정) — 진입은 경로 가드가, 저장 직전 재검사(30분 만료)는 여기서 한다 */
    const scheduleLock = useScheduleActionLock('schedule.edit');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const submissionLockRef = useRef(false);
    /**
     * 같은 등록 의도에는 같은 요청번호(operationId)를 쓴다.
     *
     * 이 화면의 실패는 "일정이 안 만들어졌다"를 뜻하지 않는다. 서버는 일정을 만든 뒤에도
     * 챗봇·푸시·알림센터를 계속 부르고, 그 구간에서 나는 오류가 그대로 HTTP 실패로 올라온다.
     * 그때 새 요청번호로 다시 누르면 공유 회차가 한 번 더 빠진다. 같은 번호를 다시 보내야
     * 서버가 앞서 만든 일정을 되돌려준다.
     *
     * ⚠️ **중복을 막는 것은 이 ref 가 아니라 서버다.** 이 ref 가 지키는 범위는
     * "이 화면이 살아 있는 동안, 직전 의도 하나"뿐이다. 아래 경우에는 전부 새 번호가 나간다.
     *  - 새로고침·다른 탭·`/schedule` 로 나갔다 되돌아오기 (컴포넌트가 새로 뜬다)
     *  - 의도 A 실패 → 의도 B 시도 → 다시 A (슬롯이 하나라 B 가 A 를 덮는다)
     *  - 공유 A 실패 → 개인 수강권 시도 (개인은 문맥이 없어 ref 가 null 로 지워진다)
     * 그래도 괜찮은 이유는, 같은 강사·회원·시각의 활성 일정이 이미 있으면 서버가 공유 lease
     * 안에서 실제로 조회해 409 로 요청 전체를 거절하기 때문이다. 여기서 하는 일은
     * "흔한 즉시 재시도를 값싸게 막고, 서버 판정을 기다리지 않게 하는 것"이다.
     */
    const sharedOperationRef = useRef<StableSharedScheduleOperation | null>(null);
    /**
     * 등록 직전 확인 창은 공통 알럿이 아니라 이 화면의 지역 모달로 띄운다. 공통 알럿은 늦게 온 다른
     * 알럿이 앞 확인 콜백을 물려받아 수업을 보낼 수 있다(scheduleConfirmDialog 주석).
     */
    const { openConfirm, closeConfirm, confirmDialog } = useScheduleConfirmDialog();
    // 수강권 일시정지 — 추가되는 회원의 수강권이 정지 기간이면 확인 창(P4)
    const pauseGate = useMembershipPauseCreateGate({ createAlert });
    /** 생성 실패 안내. 「풀고 등록」 뒤 실패면 정지가 풀렸다는 문구를 앞에 붙인다. */
    const alertCreateFailure = (title: string, message: string, ...rest: any[]) =>
        createAlert(title, pauseGate.decorateFailureMessage(message), ...rest);
    const currentTime = selectedSchedule ? dayjs(selectedSchedule?.startTime) : dayjs();
    const roundedTime = currentTime.minute(Math.floor(currentTime.minute() / 10) * 10).second(0);
    const currentTimeAdd50 = dayjs(roundedTime).add(50, 'minute');

    /**
     * coach : 담당강사
     * startTime : 시작 시간
     * endTime : 종료시간
     * isOneOnOneClass : true - 1:1수업 , false - 2:1수업
     * isDisabledInput : 1:1수업 또는 1:2 수업 클릭시 멤버가 다선택되면 input 비활성화
     */
    const [coach, setCoach] = useState({
        value: selectedSchedule ? selectedSchedule?.coachId : '',
        label: selectedSchedule ? selectedSchedule?.coachName : '강사를 선택해주세요!',
    });
    const [startTime, setStartTime] = useState({ value: roundedTime, label: dayjs(roundedTime).format('A hh:mm') });
    const [endTime, setEndTime] = useState({
        value: selectedSchedule?.endTime,
        label: dayjs(selectedSchedule?.endTime).format('A hh:mm'),
    });
    const [isOneOnOneClass, setIsOneOnOneClass] = useState<boolean>(true);
    const [isDisabledInput, setIsDisabledInput] = useState<boolean>(false);

    /**
     * searchText: 회원 검색
     * memberList: 강사의 연결된 모든 회원의 리스트
     * filteredMemberList : 검색결과가 반영된 회원 리스트
     * selectedMember: 선택된 회원
     */
    const [searchText, setSearchText] = useState<string>('');
    const [memberList, setMemberList] = useState<any>([]);
    const [filteredMemberList, setFilteredMemberList] = useState<any>([]);
    const [selectedMember, setSelectedMember] = useState<any>([]);

    const {
        selectedMembers: scheduleSelectedMember,
        excludedSharedMembershipCounts,
        isReady: areMembershipEntitlementsReady,
        isLoading: areMembershipEntitlementsLoading,
        isError: hasMembershipEntitlementsError,
        refetch: refetchMembershipEntitlements,
    } = useScheduleMembershipEntitlements({
        selectedMembers: selectedMember,
        centerId: String(authObject?.centerId || ''),
        coachId: String(coach?.value || ''),
        enabled: sharedMembershipStaffBookingEnabled === true,
    });

    const [oneOnOneMemberShipIndex, setOneOnOneMemberShipIndex] = useState<number>(1);

    const [firstMemberShipIndex, setFirstMemberShipIndex] = useState<number>(1);
    const [secondMemberShipIndex, setSecondMemberShipIndex] = useState<number>(1);

    /** 캘린더 state */
    const [selectedDay, setSelectedDay] = useState<any>(
        selectedSchedule ? new Date(selectedSchedule?.startTime) : new Date()
    );
    const [isCalendar, setIsCalendar] = useState<boolean>(false);

    const [timeList, setTimeList] = useState<any>(null); // 10분단위 시간 배열

    // 수강권 등록 모달 열기
    const [isModalMembershipCreateVisible, setIsModalMembershipCreateVisible] = useState<boolean>(false);

    // 회원 수강권 새로 생성 모달
    const [isRegisterPassModal, setIsRegisterPassModal] = useState<boolean>(false);

    // selectedMemberName : 선택된 회원의 이름
    // selectedMemberId : 선택된 회원의 userId
    const [selectedMemberName, setSelectedMemberName] = useState<string>('');
    const [selectedMemberId, setSelectedMemberId] = useState<string>('');

    // 회원 검색 이벤트
    const onChangeText = (e: any) => {
        setSearchText(e.target.value);
    };

    // 담당 강사 선택
    const handleCoachSelect = (option: any) => {
        setCoach(option);
    };

    // 시작 시간 선택
    const handleStartTimeSelect = (option: any) => {
        setStartTime(option);
    };

    // 종료 시간 선택
    const handleEndTimeSelect = (option: any) => {
        setEndTime(option);
    };

    // 멤버 선택
    const handleMemberSelect = (option: any) => {
        if (isScheduleMemberPickerReadOnly(option)) return;
        setSearchText('');
        let cloneSelectedMember = _.cloneDeep(selectedMember);
        cloneSelectedMember.push(option);
        setSelectedMember(cloneSelectedMember);
    };

    // 선택된 회원 지우는 함수
    const onClickDeleteSelectedMember = (value: string) => {
        const filterSelectedMemberList = selectedMember.filter((e: any) => e.value !== value);
        setSelectedMember(filterSelectedMemberList);

        setOneOnOneMemberShipIndex(1);
        setFirstMemberShipIndex(1);
        setSecondMemberShipIndex(1);
    };

    // 수강권 추가하기 버튼 클릭이벤트
    const onClickClassPassPage = (selectedMember: any) => {
        setSelectedMemberId(selectedMember.value);
        setSelectedMemberName(selectedMember.label);
        setIsModalMembershipCreateVisible(true);
    };

    //  회원 리스트 조회 API : (GET)
    const getMemberListForCenter = useQuery(
        ['getMemberListForCenterAPI', coach?.value],
        async () => await getMemberListForCenterAPI(authObject?.centerId, coach?.value, authorization),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    const memberListToRes = buildWritableScheduleMemberOptions(
                        res?.data?.memberDataList
                    );
                    setMemberList(memberListToRes);
                    setFilteredMemberList(memberListToRes);
                    setSelectedMember(
                        refreshSelectedScheduleMembers(selectedMember, memberListToRes)
                    );
                }
            },
            onError: (err) => {
                console.log(err);
            },
            enabled: !!authorization && !!coach?.value,
            // refetchOnWindowFocus: 'always',
            staleTime: 0,
        }
    );

    /**
     * 원인 문구 뒤에 "일정 목록에서 확인해주세요"를 덧붙인다.
     * 문구는 새로 만들지 않고 getDuetFailureFollowUpNotice 가 붙이는 것을 그대로 쓴다.
     * 원인 문구가 이미 목록 확인을 시키고 있으면 omitIfRedundantWith 가 중복을 걸러낸다.
     */
    const withScheduleListNotice = (res: any, causeMessage: string) => {
        const notice = getDuetFailureFollowUpNotice(res, {
            omitIfRedundantWith: causeMessage,
        });
        return notice ? `${causeMessage}\n\n${notice}` : causeMessage;
    };

    // 1:1수업 생성 API : (POST)
    /** 1:1 요청 내용. 의도 키도 여기서 같이 굳힌다(규칙은 예전과 같다). */
    const buildOneOnOneSubmission = (refineDate: RefineDate): OneMoreOneOnOneSubmission => {
        const membership = scheduleSelectedMember[0]?.memberships[oneOnOneMemberShipIndex - 1];
        const centerId = authObject?.centerId;
        const coachId = coach?.value;
        const memberId = scheduleSelectedMember[0]?.value;
        return {
            target: 'ONE_ON_ONE',
            centerId,
            coachId,
            coachLabel: coach?.label,
            memberId,
            membership,
            refineDate,
            intentKey: buildOneMoreScheduleIntentKey({
                centerId,
                coachId,
                memberId,
                membership,
                startTime: refineDate?.refineStartTime,
                endTime: refineDate?.refineEndTime,
            }),
        };
    };

    const createOneOnOneSchedule = useMutation({
        mutationFn: async (submission: OneMoreOneOnOneSubmission) => {
            const { membership, refineDate } = submission;
            /**
             * 공유 수강권을 골랐으면 풀 ID와 요청번호를 **둘 다** 보낸다.
             * 둘 다 없으면 서버가 요청 내용으로 결정적 키를 만들어 주지만, 풀 ID만 보내고
             * 요청번호를 빠뜨리면 400(SHARED_MEMBERSHIP_INVALID_INPUT)이다. 그래서 두 값을
             * 따로 만들지 않고 항상 한 묶음으로만 다룬다.
             * 개인 수강권이면 컨텍스트 자체가 만들어지지 않아 예전 요청 그대로 나간다.
             */
            const sharedOperation = getStableSharedScheduleOperation(
                sharedOperationRef.current,
                submission.intentKey,
                membership,
            );
            sharedOperationRef.current = sharedOperation ?? null;

            return await createOneMoreScheduleAPI(
                submission.centerId,
                submission.coachId,
                submission.memberId,
                refineDate?.refineStartTime,
                refineDate?.refineEndTime,
                membership?.id,
                authorization,
                sharedOperation?.writeContext,
            );
        },
        onSuccess: (res, { refineDate, coachLabel }) => {
            /**
             * 성공이든 실패든 목록을 다시 받는다.
             *
             * 서버는 일정을 만든 뒤 챗봇·푸시·알림센터를 이어서 부르고, 그 구간의 오류나
             * lease 해제 실패는 일정이 남은 채로 HTTP 실패가 된다. 실패 응답을 받았다고
             * 서버 상태가 그대로라고 볼 수 없다.
             *
             * invalidateQueries 만으로는 부족하다 — 관찰자가 없는 쿼리는 요청이 아예 안 나가고,
             * 이 화면(CreateSchedulePage)은 목록 화면과 다른 라우트라 목록 쿼리가 비어 있는
             * 경우가 흔하다. refreshScheduleWriteQueries 가 그 한 건을 fetchQuery 로 채운다.
             */
            refreshScheduleWriteQueries(queryClient, {
                centerId: authObject?.centerId,
                authorization,
                date: dayjs(refineDate?.refineStartTime).format('YYYY-MM-DD'),
            });

            if (res.status === 201 || res.status === 200) {
                sharedOperationRef.current = null;
                const startTime = refineDate.refineStartTime;
                navigate('/schedule', {
                    state: { calendarType, calendarTimeInfo: startTime },
                    replace: true,
                });
                return;
            }

            /**
             * 요청번호를 버릴지는 서버의 retryPolicy 로만 판단한다. "남은 것이 있나"와는
             * 다른 질문이라 서로의 근거로 쓰지 않는다.
             *
             * 단건 경로는 응답에 retryPolicy 가 실리지 않아(그건 duplicable 전용) 프론트
             * 폴백 목록이 판정한다. 그 목록은 서버의 NEW_KEY_REQUIRED_ERROR_CODES
             * (schedule.service.js:733-741)와 같은 세 코드다 — 특히 OPERATION_MISMATCH 는
             * **서버가 새 키를 요구하는 계약**이다. 같은 키를 붙들면 내용이 다른 한 영원히
             * 같은 오류가 돌아온다. 새 키로 재제출해도 이미 만들어진 일정은 서버의 활성
             * 일정 조회가 409 로 막는다.
             */
            if (shouldDiscardSharedScheduleOperation(res)) {
                sharedOperationRef.current = null;
            }

            const sharedErrorMessage = getSharedScheduleErrorMessage(res);
            if (sharedErrorMessage) {
                /**
                 * 공유 실패에는 기본적으로 목록 확인을 붙이지 않는다. 대부분 쓰기 전에
                 * 확정된 거절이라(회차 부족·권한·입력 오류) "이미 만들어졌을 수 있습니다"를
                 * 붙이면 서로 모순되는 안내가 된다.
                 *
                 * 예외는 서버가 그 코드를 낼 시점에 일정이 이미 있을 수 있는 경우다.
                 * 특히 OPERATION_MISMATCH 는 서버가 활성 일정을 **찾은 뒤에만** 던지므로,
                 * 여기서 안내를 빼면 직원이 이미 등록된 일정을 못 보고 하나 더 만든다.
                 */
                alertCreateFailure(
                    '공유 수강권 확인',
                    isPostWriteSharedScheduleFailure(res)
                        ? withScheduleListNotice(res, sharedErrorMessage)
                        : sharedErrorMessage,
                    true
                );
                return;
            }

            if (res.status === 410 || res.status === 420 || res.status === 430) {
                alertCreateFailure(
                    '중복',
                    `선택된 담당 강사 ${coachLabel}님이\n해당시간에 중복된 스케줄이 존재합니다`,
                    true
                );
                return;
            }

            /**
             * 원인을 특정하지 못한 실패다. 여기가 바로 "일정은 만들어졌는데 푸시가 실패한"
             * 경우가 떨어지는 자리라, 재시도 전에 목록부터 보게 한다.
             * 응답을 못 받은 경우(status 0)도 서버는 계속 처리했을 수 있어 같게 다룬다.
             */
            const causeMessage =
                res.status === 0
                    ? '회원 추가 요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요'
                    : '서버 내부 오류\n지속적인 오류발생시 고객센터로 문의바랍니다';
            alertCreateFailure('오류', withScheduleListNotice(res, causeMessage), true);
        },
        onError: (_error, { refineDate }) => {
            refreshScheduleWriteQueries(queryClient, {
                centerId: authObject?.centerId,
                authorization,
                date: dayjs(refineDate?.refineStartTime).format('YYYY-MM-DD'),
            });
            alertCreateFailure(
                '오류',
                '회원 추가 요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요\n\n일정이 이미 만들어졌을 수 있습니다\n일정 목록에서 확인해주세요.',
                true,
            );
        },
        onSettled: () => {
            submissionLockRef.current = false;
        },
    });

    /**
     * 1:2수업 생성 API : (POST /duplicable)
     *
     * ⚠️ **지금은 쓰이지 않는다.** 이 화면에는 1:1 / 1:2 모드 전환 UI 가 없어
     * (`CreateOneMoreScheduleRight` 가 `setIsOneOnOneClass` 를 받기만 하고 부르지 않는다)
     * `isOneOnOneClass` 는 항상 true 다. 제출 판정도 1:1 모드에서는 이 경로를 고르지
     * 않는다(`resolveScheduleSubmitPlan`).
     *
     * ⚠️ **되살리려면 공유 문맥 배선이 먼저다.** 아래 mutation 은 `membershipId` 만 보내고
     * `membershipSharePoolId` · `operationId` 를 보내지 않는다. 공유 수강권이 목록에 뜨는
     * 지금 상태에서 그대로 열면, 서버가 `membershipId` 로 활성 풀을 찾아 풀 ID 를 주입하는
     * 바람에 "풀 ID 는 있는데 요청번호가 없는" 요청이 되어 **400
     * (SHARED_MEMBERSHIP_INVALID_INPUT)** 이 난다. `CreateOneSchedule` 의
     * `buildOneOnTwoRequest` 처럼 항목마다 풀 ID 를 싣고 안정 요청번호를 함께 보내야 한다.
     */
    const buildOneOnTwoSubmission = (refineDate: RefineDate): OneMoreOneOnTwoSubmission => {
        const memberships = [
            scheduleSelectedMember[0]?.memberships[firstMemberShipIndex - 1],
            scheduleSelectedMember[1]?.memberships[secondMemberShipIndex - 1],
        ];
        return {
            target: 'ONE_ON_TWO',
            centerId: authObject?.centerId,
            coachId: coach?.value,
            scheduleTimes: [0, 1].map((index) => ({
                memberId: selectedMember[index].value,
                startTime: refineDate?.refineStartTime,
                endTime: refineDate?.refineEndTime,
                membershipId: memberships[index]?.id,
            })),
            memberships,
            refineDate,
        };
    };

    const createOneOnTwoSchedule = useMutation({
        mutationFn: async (submission: OneMoreOneOnTwoSubmission) =>
            await createOneOnTwoScheduleAPI(
                submission.centerId,
                submission.coachId,
                submission.scheduleTimes,
                authorization
            ),
        onSuccess: (res, { refineDate }) => {
            // 1:2는 실패해도 앞서 만들어진 일정을 되돌리지 않는다. 성공이든 실패든
            // 서버 상태가 바뀌었을 수 있으므로 목록을 캐시 유무와 무관하게 다시 받는다.
            refreshScheduleWriteQueries(queryClient, {
                centerId: authObject?.centerId,
                authorization,
                date: dayjs(refineDate?.refineStartTime).format('YYYY-MM-DD'),
            });

            if (res.status === 201) {
                const startTime = refineDate.refineStartTime;
                navigate('/schedule', {
                    state: { calendarType, calendarTimeInfo: startTime },
                    replace: true,
                });
                return;
            }

            // 원인 문구가 먼저, 후속 안내가 그 뒤에 붙는다. 알럿은 하나만 띄운다.
            // 푸시·챗봇 실패처럼 부분 성공 필드가 아예 없는 실패도 목록 확인을 안내한다(계약 §1-4).
            const withPartialNotice = (body: string) => {
                const notice = getDuetFailureFollowUpNotice(res, {
                    resolveMemberName: (memberId: string) =>
                        selectedMember.find((member: any) => member?.value === memberId)?.label ||
                        '',
                    omitIfRedundantWith: body,
                });
                return notice ? `${body}\n\n${notice}` : body;
            };

            // 중복 일정(410·420·430) 분기는 두지 않는다. 이 엔드포인트(duplicable)는 그 코드를
            // 내지 않는다 — 컨트롤러가 preCheck·isDuplicateTimes 를 부르지 않고, 개인 항목이 타는
            // addScheduleV2 에도 해당 throw 가 없다(420 을 던지는 checkSchedulesForBlock 은
            // 휴무 블록 전용이라 이 경로와 무관하다).
            const sharedErrorMessage = getSharedScheduleErrorMessage(res);
            if (sharedErrorMessage) {
                // 공유 수강권 실패는 다시 눌러도 같은 결과다. 1:1 경로와 같은 문구를 쓴다.
                alertCreateFailure('공유 수강권 확인', withPartialNotice(sharedErrorMessage), true);
            } else if (res.status === 0) {
                // 응답을 못 받았다. 서버는 계속 처리했을 수 있으므로 재시도 전에 목록부터 보게 한다.
                alertCreateFailure(
                    '오류',
                    withPartialNotice(
                        '회원 추가 요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요',
                    ),
                    true
                );
            } else {
                alertCreateFailure(
                    '오류',
                    withPartialNotice('서버 내부 오류\n지속적인 오류발생시 고객센터로 문의바랍니다'),
                    true
                );
            }
        },
        onError: (_error, { refineDate }) => {
            refreshScheduleWriteQueries(queryClient, {
                centerId: authObject?.centerId,
                authorization,
                date: dayjs(refineDate?.refineStartTime).format('YYYY-MM-DD'),
            });
            alertCreateFailure(
                '오류',
                '회원 추가 요청에 실패했습니다\n네트워크 상태를 확인한 뒤 다시 시도해주세요\n\n일정이 이미 만들어졌을 수 있습니다\n일정 목록에서 확인해주세요.',
                true,
            );
        },
        onSettled: () => {
            submissionLockRef.current = false;
        },
    });

    const submitOneOnOneSchedule = (submission: OneMoreOneOnOneSubmission) => {
        if (submissionLockRef.current) return;
        submissionLockRef.current = true;
        createOneOnOneSchedule.mutate(submission);
    };

    const submitOneOnTwoSchedule = (submission: OneMoreOneOnTwoSubmission) => {
        if (submissionLockRef.current) return;
        submissionLockRef.current = true;
        createOneOnTwoSchedule.mutate(submission);
    };

    // 수업일정 등록 버튼 클릭이벤트
    const onClickCreateSchedule = () => {
        if (submissionLockRef.current) return;
        // 새 제출 시도는 앞 확인 창의 시도를 끊는다(어떤 검사에서 멈추든 앞 "그래도 잡기"가 남지 않게).
        closeConfirm();

        if (coach?.value === '') {
            createAlert('필수 입력', '담당 강사를 선택해주세요', true);
            return;
        }

        if (selectedMember.length === 0) {
            createAlert('필수 입력', '회원을 선택해주세요', true);
            return;
        }

        if (selectedMember.some(isScheduleMemberPickerReadOnly)) {
            createAlert('등록 불가', '탈퇴 회원은 새 수업에 등록할 수 없습니다', true);
            return;
        }

        if (
            sharedMembershipStaffBookingEnabled === true &&
            !areMembershipEntitlementsReady
        ) {
            const isEntitlementLoading =
                areMembershipEntitlementsLoading && !hasMembershipEntitlementsError;
            // 조회가 에러로 굳으면 react-query가 스스로 재조회하지 않는다.
            // '다시 시도' 안내가 실제로 동작하도록 재조회 버튼을 함께 준다.
            createAlert(
                '수강권 확인 필요',
                isEntitlementLoading
                    ? '공유 수강권 사용 권한을 확인하고 있습니다\n잠시 후 다시 시도해주세요'
                    : '공유 수강권 사용 권한을 확인하지 못했습니다\n네트워크 상태를 확인한 뒤 다시 조회해주세요',
                true,
                isEntitlementLoading ? '확인' : '다시 조회',
                isEntitlementLoading ? undefined : () => refetchMembershipEntitlements(),
            );
            return;
        }

        let findNullMembership = scheduleSelectedMember.findIndex(
            (item: any) => item?.memberships.length === 0,
        );

        if (findNullMembership !== -1) {
            // 목록에서 공유 수강권이 빠지는 이유는 이제 하나뿐이다 — 고른 강사가 그 수강권의
            // 담당 강사가 아닌 경우. 이걸 알리지 않으면 직원이 있는 수강권을 또 발급한다.
            createAlert(
                '필수 입력',
                excludedSharedMembershipCounts[findNullMembership] > 0
                    ? '이 회원의 공유 수강권은 담당 강사의 수업에서만 등록할 수 있습니다\n수강권의 담당 강사를 선택하거나 개인 수강권을 사용해주세요'
                    : '수강권이 없는 회원이 존재합니다\n반드시 수강권을 만들고 일정을 만들어주세요',
                true
            );
            return;
        }

        // 2:1 수업일경우
        if (!isOneOnOneClass) {
            if (selectedMember.length !== 2) {
                createAlert('필수 입력', '1:2 수업일 경우 회원을 2명 선택해주세요', true);
                return;
            }
        }

        const concatStartTime = dayjs(concatDateAndTimeDayjs(selectedDay, startTime.value));
        const concatEndTime = dayjs(concatDateAndTimeDayjs(selectedDay, endTime.value));
        const refineStartTime = dayjs(concatStartTime).toISOString();
        const refineEndTime =
            dayjs(concatEndTime).format('HH:mm') === '00:00'
                ? dayjs(concatEndTime).add(1, 'day').toISOString()
                : dayjs(concatEndTime).toISOString();
        if (isSameDate(dayjs(refineStartTime), dayjs(refineEndTime))) {
            createAlert('입력 오류', '시작 시간과 완료 시간이 같습니다!\n다시 한번 확인해주세요!', '확인', () => {});
            return;
        }

        if (dayjs(refineStartTime).isAfter(dayjs(refineEndTime))) {
            createAlert(
                '입력 오류',
                '시작 시간이 완료 시간보다 늦을 수  없습니다!\n다시 한번 확인해주세요!',
                '확인',
                () => {}
            );
            return;
        }

        const refineDate: RefineDate = {
            refineStartTime: refineStartTime,
            refineEndTime: refineEndTime,
        };

        /**
         * 제출 방식 판정은 순수 모듈 한곳에서 한다.
         *
         * 예전에는 여기 조건식이 `||` 로 이어져 있었고 **두 번째 항에만 모드 가드가
         * 빠져 있었다.** 그래서 1:1 모드인데 두 번째 회원의 잔여가 0이면 1:2 등록이
         * 나갔다 — 화면에 그리지도 않는 회원 때문에 일정이 2건 만들어진다.
         * 공유와 무관한 원래 버그이며, 여기서 함께 닫는다.
         */
        const submitPlan = resolveScheduleSubmitPlan({
            isOneOnOneClass,
            oneOnOneMembership:
                scheduleSelectedMember[0]?.memberships[oneOnOneMemberShipIndex - 1],
            firstMembership:
                scheduleSelectedMember[0]?.memberships[firstMemberShipIndex - 1],
            secondMembership:
                scheduleSelectedMember[1]?.memberships[secondMemberShipIndex - 1],
        });

        // 공유 풀은 개인 수강권과 달리 잔여 회차를 초과해서 등록할 수 없다.
        // 날짜 경고보다 먼저 막는다 — "그래도 잡기"가 이 차단을 우회하면 안 된다.
        if (submitPlan.kind === 'SHARED_EXHAUSTED_BLOCKED') {
            createAlert(
                '등록 가능 회차 부족',
                '공유 수강권의 등록 가능 회차가 남아있지 않습니다\n기존 수업을 취소하거나 공유 수강권을 추가한 뒤 다시 시도해주세요',
                true,
            );
            return;
        }

        // 보낼 요청 내용을 여기서 한 번 만든다. 판정·확인 창·전송이 모두 이 객체를 쓴다.
        const submission: OneMoreScheduleSubmission =
            submitPlan.submit === 'ONE_ON_ONE'
                ? buildOneOnOneSubmission(refineDate)
                : buildOneOnTwoSubmission(refineDate);

        const findMemberName = (memberId: string) =>
            selectedMember.find((member: any) => member?.value === memberId)?.label || '';
        const dateWarningItems: ScheduleDateWarningItem[] =
            submission.target === 'ONE_ON_ONE'
                ? [
                      {
                          memberId: submission.memberId,
                          memberName: findMemberName(submission.memberId),
                          membershipId: submission.membership?.id,
                          membershipSharePoolId: submission.membership?.membershipSharePoolId,
                          times: [
                              {
                                  startTime: submission.refineDate.refineStartTime,
                                  endTime: submission.refineDate.refineEndTime,
                              },
                          ],
                      },
                  ]
                : submission.scheduleTimes.map((item) => ({
                      memberId: item.memberId,
                      memberName: findMemberName(item.memberId),
                      membershipId: item.membershipId,
                      times: [{ startTime: item.startTime, endTime: item.endTime }],
                  }));
        const dateWarnings = findScheduleDateWarnings(
            dateWarningItems,
            scheduleSelectedMember.flatMap((member: any) => member?.memberships || []),
        );

        const send = async () => {
            // 날짜 경고까지 통과한 뒤, 보내기 직전에 정지 확인(취소·해제 실패면 보내지 않는다)
            const startTimes = [submission.refineDate.refineStartTime];
            const pauseTargets =
                submission.target === 'ONE_ON_ONE'
                    ? [toPtPauseGateTarget(submission.membership, findMemberName(submission.memberId), startTimes)]
                    : submission.memberships.map((membership: any, index: number) =>
                          toPtPauseGateTarget(
                              membership,
                              findMemberName(submission.scheduleTimes[index]?.memberId),
                              startTimes,
                          ),
                      );
            if (submissionLockRef.current) return;
            // 스케줄 동작 잠금 재검사 — 정지 해제(첫 부수 쓰기)보다 **먼저** 본다(설계 §6-6)
            if (scheduleLock.requiresCheck() && !(await scheduleLock.ensure())) return;
            if (submissionLockRef.current) return;
            if (!(await pauseGate.confirmBeforeCreate(compactTargets(pauseTargets)))) return;
            if (submission.target === 'ONE_ON_ONE') {
                submitOneOnOneSchedule(submission);
                return;
            }
            submitOneOnTwoSchedule(submission);
        };

        const showMemberName = dateWarningItems.length > 1;
        // 공유 기간 밖은 차단(전송 0회), 개인만 걸리면 확인 창, 다 쓴 수강권과 겹치면 한 창.
        dispatchScheduleSubmitDecision({
            decision: resolveScheduleSubmitDecision(submitPlan, dateWarnings),
            createAlert,
            openConfirm,
            closeConfirm,
            send,
            buildConfirmContent: ({ exhausted, dateWarnings: personalWarnings }) =>
                buildScheduleConfirmAlertContent({
                    exhausted,
                    dateWarningMessage: formatScheduleDateWarningMessage(personalWarnings, {
                        showMemberName,
                    }),
                }),
            buildSharedDateBlockMessage: (sharedWarnings) =>
                formatSharedDateBlockMessage(sharedWarnings, { showMemberName }),
        });
    };
    // 회원이름 검색 정제
    useEffect(() => {
        if (memberList) {
            if (searchText !== '') {
                let filterMemberList = memberList.filter((e: any) => {
                    return (
                        e.label.includes(searchText) &&
                        !selectedMember.some((selected: any) => selected.value === e.value)
                    );
                });

                setFilteredMemberList(filterMemberList);
            } else {
                let filterMemberList = memberList.filter((e: any) => {
                    return !selectedMember.some((selected: any) => selected.value === e.value);
                });

                setFilteredMemberList(filterMemberList);
            }
        }
    }, [searchText, selectedMember]);

    // 시간 10분단위로 생성
    useEffect(() => {
        // 시간생성
        const generateTimeSlots = () => {
            const timeSlots = [];
            const start = dayjs().startOf('day').hour(0).minute(0);
            for (let i = 0; i < 144; i++) {
                // 24 hours * 6 (10분 간격)
                const time = start.add(i * 10, 'minute');
                const formattedTime = time.format('A hh:mm'); // 오전/오후 표시
                timeSlots.push({ value: time, label: formattedTime });
            }
            return timeSlots;
        };

        const timeSlots = generateTimeSlots();
        setTimeList(timeSlots);
    }, []);

    //  1:1수업 2:1 수업일시 input disable 로직
    useEffect(() => {
        if (isOneOnOneClass) {
            selectedMember && selectedMember.length === 1 ? setIsDisabledInput(true) : setIsDisabledInput(false);
        } else {
            selectedMember && selectedMember.length === 2 ? setIsDisabledInput(true) : setIsDisabledInput(false);
        }
    }, [selectedMember]);

    // 1:1수업 , 2:1 수업 버튼 클릭시 상태 초기화
    useEffect(() => {
        setSelectedMember([]);
        setSearchText('');
    }, [isOneOnOneClass]);

    return (
        <Col style={{ paddingLeft: 101, paddingTop: 56, paddingRight: 101, paddingBottom: 64 }}>
            {/* 스케줄 동작 잠금 — 저장 직전 재검사 카드(body 포털) */}
            {scheduleLock.gate}
            {/* 등록 직전 확인 창(화면 지역, 고정 위치 덮개) */}
            {confirmDialog}
            {pauseGate.modal}
            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Row style={{ alignItems: 'center' }}>
                    <div
                        style={{ cursor: 'pointer', marginRight: 16, display: 'flex', alignItems: 'center' }}
                        onClick={() => {
                            navigate('/schedule', {
                                state: { calendarType, calendarSelectedDay, calendarScrollY },
                                replace: true,
                            });
                        }}
                    >
                        <SvgIcon name={'SvgLeftStrokeArrow'} size={20} stroke={colors.black} fill={colors.trans} />
                    </div>
                    <Subtitle1Bold style={{ color: colors.LAVEL_4 }}>{'회원 추가하기'}</Subtitle1Bold>
                    <Body3Regular style={{ marginLeft: 16, color: colors.LAVEL_1 }}>
                        {'기존 수업에 회원님을 추가하여 1:2 수업으로 변경 가능해요!'}
                    </Body3Regular>
                </Row>

                <ColorSquareButton
                    isGradient={true}
                    customStyle={{ width: 136, height: 36 }}
                    text="회원 추가 완료"
                    size="small"
                    isDisabled={createOneOnOneSchedule.isLoading || createOneOnTwoSchedule.isLoading}
                    callBack={onClickCreateSchedule}
                />
            </Row>

            <Row style={{ marginTop: 56 }}>
                <CreateOneMoreScheduleLeft
                    coachOption={coachOption}
                    coach={coach}
                    handleCoachSelect={handleCoachSelect}
                    selectedDay={selectedDay}
                    setIsCalendar={setIsCalendar}
                    timeList={timeList}
                    startTime={startTime}
                    handleStartTimeSelect={handleStartTimeSelect}
                    endTime={endTime}
                    handleEndTimeSelect={handleEndTimeSelect}
                    // onClickAddTime={onClickAddTime}
                    // onClickSubTime={onClickSubTime}
                />
                <CreateOneMoreScheduleRight
                    setIsOneOnOneClass={setIsOneOnOneClass}
                    isOneOnOneClass={isOneOnOneClass}
                    selectedMember={scheduleSelectedMember}
                    filteredMemberList={filteredMemberList}
                    handleMemberSelect={handleMemberSelect}
                    searchText={searchText}
                    onChangeText={onChangeText}
                    coach={coach}
                    isDisabledInput={isDisabledInput}
                    onClickDeleteSelectedMember={onClickDeleteSelectedMember}
                    onClickClassPassPage={onClickClassPassPage}
                    oneOnOneMemberShipIndex={oneOnOneMemberShipIndex}
                    setOneOnOneMemberShipIndex={setOneOnOneMemberShipIndex}
                    firstMemberShipIndex={firstMemberShipIndex}
                    setFirstMemberShipIndex={setFirstMemberShipIndex}
                    secondMemberShipIndex={secondMemberShipIndex}
                    setSecondMemberShipIndex={setSecondMemberShipIndex}
                    excludedSharedMembershipCounts={excludedSharedMembershipCounts}
                />
            </Row>

            {isCalendar && (
                <Calendar
                    selectedDay={selectedDay}
                    setSelectedDay={setSelectedDay}
                    setIsCalendar={setIsCalendar}
                    isPrevMonth={true}
                    isNextMonth={true}
                    style={{
                        width: 420,
                        height: 370,
                        borderRadius: 24,
                        backgroundColor: colors.WHITE_50,
                        boxShadow: '1px 1px 8px 0px #2B529D2E',
                        position: 'absolute',
                        zIndex: 1999,
                        marginTop: 340,
                    }}
                />
            )}

            {isModalMembershipCreateVisible && (
                <ModalMembershipCreate
                    setIsVisible={setIsModalMembershipCreateVisible}
                    onClickCreateNewMembership={() => setIsRegisterPassModal(true)}
                    memberName={selectedMemberName}
                    coachName={coach?.label}
                    coachId={coach?.value}
                    memberId={selectedMemberId}
                    createAlert={createAlert}
                    type={'schedule'}
                />
            )}

            {isRegisterPassModal && (
                <ModalRegisterPass
                    modalVisible={isRegisterPassModal}
                    setModalVisible={setIsRegisterPassModal}
                    createAlert={createAlert}
                />
            )}
        </Col>
    );
};

export default CreateOneMoreSchedule;
